import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { scenes as mockScenes, sceneStats as mockSceneStats } from "@/mocks/scenes";
import type { Scene } from "@/mocks/scenes";
import { useApi } from "@/hooks/useApi";
import { apiSend } from "@/lib/api";
import SceneFormModal from "./components/SceneFormModal";
import { confirmDialog, toast } from "@/components/ui/feedback";

type SortKey =
  | "seq"
  | "name"
  | "lat"
  | "triggerRadius"
  | "visibility"
  | "audioStatus"
  | "knowledgeCount"
  | "status";

function sortValue(s: Scene, key: SortKey): number | string {
  switch (key) {
    case "seq":
      return s.seq ?? Number.MAX_SAFE_INTEGER;
    case "lat":
      return s.lat;
    case "triggerRadius":
      return s.triggerRadius;
    case "knowledgeCount":
      return s.knowledgeCount;
    case "audioStatus": {
      // generated > pending > none, so asc shows未生成在前
      const order = { none: 0, pending: 1, generated: 2 } as const;
      return order[s.audioStatus] ?? 0;
    }
    default:
      return s[key];
  }
}

interface SceneGroup { id: string; name: string; seq: number; count: number }

// 每个 scene 在 routes 里被引用的方向 + 最早 seq —— /scenes/directional-index 返回的形态
type DirectionSlot = { minSeq: number; routeCount: number };
type SceneDirIndex = Record<string, { up?: DirectionSlot; down?: DirectionSlot }>;

type DirTab = "up" | "down" | "unassigned" | "all";

const DIR_TABS: { key: DirTab; label: string; icon: string }[] = [
  { key: "up",         label: "上行点位",  icon: "ri-arrow-up-line" },
  { key: "down",       label: "下行点位",  icon: "ri-arrow-down-line" },
  { key: "unassigned", label: "未分配",    icon: "ri-question-line" },
  { key: "all",        label: "全部",      icon: "ri-list-check" },
];

const COLLAPSE_KEY = "sanxia:collapsedSceneGroups";
const DIR_TAB_KEY = "sanxia:scenesDirTab";

function ScenesPage() {
  const navigate = useNavigate();
  const { data: scenes, reload: reloadScenes } = useApi<typeof mockScenes>("/scenes", mockScenes);
  const { data: sceneStats, reload: reloadStats } = useApi<typeof mockSceneStats>("/scenes/stats", mockSceneStats);
  const { data: groups, reload: reloadGroups } = useApi<SceneGroup[]>("/scene-groups", []);
  // 每个 scene 在游线里出现的方向 + 最早 seq；mock 时为空对象，前端走 "未分配" 行为
  const { data: dirIndex } = useApi<SceneDirIndex>("/scenes/directional-index", {});

  // 上行 / 下行 / 未分配 / 全部 —— 持久化到 localStorage，不让刷新跳回默认
  const [dirTab, setDirTab] = useState<DirTab>(() => {
    const saved = localStorage.getItem(DIR_TAB_KEY);
    return (saved === "up" || saved === "down" || saved === "unassigned" || saved === "all") ? saved : "up";
  });
  const setDirTabPersisted = (t: DirTab) => { setDirTab(t); localStorage.setItem(DIR_TAB_KEY, t); };

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [showCreate, setShowCreate] = useState(false);
  const [createGroupId, setCreateGroupId] = useState(""); // pre-selected group for 组内新建
  const [editTarget, setEditTarget] = useState<Scene | null>(null);

  // Group sections: collapse state persisted across reloads (exguide pattern).
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "[]") as string[]); }
    catch { return new Set(); }
  });
  const toggleCollapse = (gid: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  // Group name modal (create / rename) — styled, no native prompt.
  const [groupModal, setGroupModal] = useState<{ mode: "create" | "rename"; id?: string; name: string } | null>(null);
  const saveGroupModal = async () => {
    if (!groupModal || !groupModal.name.trim()) { toast("请输入分组名称", "info"); return; }
    try {
      if (groupModal.mode === "create") {
        await apiSend("POST", "/scene-groups", { name: groupModal.name.trim() });
        toast("分组已创建", "success");
      } else {
        await apiSend("PUT", `/scene-groups/${groupModal.id}`, { name: groupModal.name.trim() });
        toast("分组已改名", "success");
      }
      setGroupModal(null);
      reloadGroups();
    } catch (e) { toast(`保存失败: ${e instanceof Error ? e.message : e}`, "error"); }
  };
  const deleteGroup = async (g: SceneGroup) => {
    if (!(await confirmDialog({ title: "删除分组", message: `确认删除分组「${g.name}」？\n组内 ${g.count} 个点位不会被删除，将变为未分组。`, confirmText: "删除" }))) return;
    try {
      await apiSend("DELETE", `/scene-groups/${g.id}`);
      toast("分组已删除", "success");
      reloadGroups(); reloadScenes();
    } catch (e) { toast(`删除失败: ${e instanceof Error ? e.message : e}`, "error"); }
  };
  const [sortKey, setSortKey] = useState<SortKey>("seq");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const reload = () => {
    reloadScenes();
    reloadStats();
  };

  const deleteScene = async (scene: Scene) => {
    const ok = await confirmDialog({ title: "删除点位", message: `确认删除点位「${scene.name}」？\n其知识库、图片、讲解音频将一并删除，不可撤销。`, confirmText: "删除" });
    if (!ok) return;
    try {
      await apiSend("DELETE", `/scenes/${scene.id}`);
      reload();
    } catch (e) {
      toast(`删除失败: ${e instanceof Error ? e.message : e}`, "error");
    }
  };

  // 各 tab 下的 scenes 集合 + 顺序 —— 上行/下行用 dirIndex 排序，全部/未分配走当前排序键
  const filteredScenes = useMemo(() => {
    const matchesQuery = (s: Scene) => {
      const q = searchQuery === "" || s.name.includes(searchQuery);
      const st = statusFilter === "all" || s.status === statusFilter;
      return q && st;
    };
    const all = scenes.filter(matchesQuery);

    if (dirTab === "up" || dirTab === "down") {
      // 只取该方向上有引用的 scenes，并按 dirIndex 的 minSeq 升序排
      const indexKey = dirTab; // 'up' | 'down'
      const withSeq = all
        .map((s) => {
          const slot = dirIndex[String(s.id)]?.[indexKey];
          return slot ? { scene: s, minSeq: slot.minSeq } : null;
        })
        .filter((x): x is { scene: Scene; minSeq: number } => !!x);
      withSeq.sort((a, b) => a.minSeq - b.minSeq);
      return withSeq.map((x) => x.scene);
    }

    if (dirTab === "unassigned") {
      // 没在任何 routes 出现过的 scenes
      const remaining = all.filter((s) => !dirIndex[String(s.id)]);
      return [...remaining].sort((a, b) => {
        const va = sortValue(a, sortKey); const vb = sortValue(b, sortKey);
        const dir = sortDir === "asc" ? 1 : -1;
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
        return String(va).localeCompare(String(vb), "zh-CN") * dir;
      });
    }

    // tab=all 走原排序键
    const dir = sortDir === "asc" ? 1 : -1;
    return [...all].sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "zh-CN") * dir;
    });
  }, [scenes, searchQuery, statusFilter, sortKey, sortDir, dirTab, dirIndex]);

  // 各 tab 数量徽章 —— 永远显示，无视搜索条件，方便用户切换
  const tabCounts = useMemo(() => {
    let up = 0, down = 0, unassigned = 0;
    for (const s of scenes) {
      const idx = dirIndex[String(s.id)];
      if (idx?.up) up++;
      if (idx?.down) down++;
      if (!idx) unassigned++;
    }
    return { up, down, unassigned, all: scenes.length };
  }, [scenes, dirIndex]);

  const audioStatusConfig = {
    generated: { label: "已生成", color: "bg-emerald-100 text-emerald-700" },
    pending: { label: "待生成", color: "bg-amber-100 text-amber-700" },
    none: { label: "未生成", color: "bg-gray-100 text-gray-700" },
  };

  const sceneTable = (list: Scene[]) => (
    <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {(
                    [
                      ["seq", "编号"],
                      ["name", "点位名称"],
                      ["lat", "位置"],
                      ["triggerRadius", "触发半径"],
                      ["audioStatus", "讲解音频"],
                      ["knowledgeCount", "知识块"],
                    ] as [SortKey, string][]
                  ).map(([key, label]) => (
                    <th
                      key={key}
                      onClick={() => toggleSort(key)}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {sortKey === key ? (
                          <i className={`${sortDir === "asc" ? "ri-arrow-up-s-fill" : "ri-arrow-down-s-fill"} text-teal-600`} />
                        ) : (
                          <i className="ri-expand-up-down-line text-gray-300" />
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {list.map((scene) => {
                  // 上行/下行 tab 下，行号显示 routes 里的 minSeq；其它 tab 走原 seq
                  const dirSlot = (dirTab === "up" || dirTab === "down") ? dirIndex[String(scene.id)]?.[dirTab] : undefined;
                  const seqShown = dirSlot ? dirSlot.minSeq : (scene.seq ?? "-");
                  return (
                  <tr key={scene.id} className="hover:bg-teal-50/30 transition-colors cursor-pointer border-l-[3px] border-transparent hover:border-teal-500" onClick={() => navigate(`/knowledge/scenes/${scene.id}`)}>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500 font-mono">{seqShown}</span>
                      {dirSlot && dirSlot.routeCount > 1 && (
                        <span className="ml-1 text-[10px] text-gray-400" title={`出现在 ${dirSlot.routeCount} 条游线中`}>×{dirSlot.routeCount}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{scene.name}</span>
                        {scene.isStandard ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-teal-100 text-teal-700" title="标准点位 · 可被其它游线复用">
                            <i className="ri-shield-check-line mr-0.5" />标准
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500" title="私有点位 · 仅本游线使用">
                            私有
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 font-mono">{scene.lat}, {scene.lng}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{scene.triggerRadius}m</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${audioStatusConfig[scene.audioStatus].color}`}>
                        {audioStatusConfig[scene.audioStatus].label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{scene.knowledgeCount}</span>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/knowledge/scenes/${scene.id}`)}
                          title="查看内容库"
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                        >
                          <i className="ri-eye-line" />
                        </button>
                        <button
                          onClick={() => setEditTarget(scene)}
                          title="编辑点位"
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          onClick={() => deleteScene(scene)}
                          title="删除点位"
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">点位内容</h1>
          <p className="text-sm text-gray-500 mt-1">维护两坝一峡沿线点位的位置、触发半径、知识库与默认讲解音频</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap"
        >
          <i className="ri-add-line mr-1.5" />新增点位
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {[
          { label: "点位总数", value: sceneStats.totalScenes, color: "text-gray-900", bg: "bg-white", border: "border-gray-200" },
          { label: "已激活", value: sceneStats.activeScenes, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
          { label: "知识块总数", value: sceneStats.knowledgeCount, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
          { label: "已生成讲解音频", value: sceneStats.audioGenerated, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.bg} border ${stat.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        {/* 方向 tab —— 把全部点位库按"在游线里被引用的方向"切成 上行 / 下行 / 未分配 / 全部 4 栏 */}
        <div className="flex items-center gap-1 border-b border-gray-200 mb-3">
          {DIR_TABS.map((t) => {
            const active = dirTab === t.key;
            const count = tabCounts[t.key];
            return (
              <button
                key={t.key}
                onClick={() => setDirTabPersisted(t.key)}
                className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                  active
                    ? "border-teal-600 text-teal-700"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                <i className={t.icon} />
                {t.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[11px] ${active ? "bg-teal-100 text-teal-700" : "bg-gray-100 text-gray-500"}`}>
                  {count}
                </span>
              </button>
            );
          })}
          <span className="ml-auto text-xs text-gray-400 pb-2">
            {dirTab === "up"   && "按上行航线顺序排列"}
            {dirTab === "down" && "按下行航线顺序排列"}
            {dirTab === "unassigned" && "未出现在任何游线里的点位"}
            {dirTab === "all"  && "所有点位 · 按自定义分组展示"}
          </span>
        </div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-56">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="搜索点位名称" className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
              <option value="all">全部状态</option>
              <option value="active">已激活</option>
              <option value="inactive">未激活</option>
            </select>
            {(searchQuery || statusFilter !== "all") && (
              <button onClick={() => { setSearchQuery(""); setStatusFilter("all"); }} className="text-sm text-gray-500 hover:text-gray-700">重置</button>
            )}
          </div>
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button onClick={() => setViewMode("list")} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <i className="ri-list-check mr-1" />列表
            </button>
            <button onClick={() => setViewMode("map")} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === "map" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <i className="ri-map-2-line mr-1" />地图
            </button>
          </div>
        </div>

        {viewMode === "list" ? (
          dirTab !== "all" ? (
            // 上行 / 下行 / 未分配 —— 直接一张顺序表，不再叠 scene_group 分组（避免视觉两层嵌套）
            <div className="border border-gray-200 rounded-lg">
              <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-700">
                <i className={dirTab === "up" ? "ri-arrow-up-line text-teal-600" : dirTab === "down" ? "ri-arrow-down-line text-teal-600" : "ri-question-line text-gray-400"} />
                <span className="font-medium">
                  {dirTab === "up" && "上行点位"}
                  {dirTab === "down" && "下行点位"}
                  {dirTab === "unassigned" && "未分配（不在任何游线里）"}
                </span>
                <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">
                  {filteredScenes.length}
                </span>
                <span className="ml-auto text-xs text-gray-400">
                  {(dirTab === "up" || dirTab === "down") && "在「游线导览」中调整该方向的途径与顺序"}
                </span>
              </div>
              {filteredScenes.length > 0 ? sceneTable(filteredScenes) : (
                <p className="px-4 pb-4 text-sm text-gray-400">
                  {dirTab === "unassigned" ? "所有点位都已在游线中使用。" : "暂无点位 —— 先去游线编辑器把点位加进该方向。"}
                </p>
              )}
            </div>
          ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">按分组分区（共 {groups.length} 组）</span>
              <button onClick={() => setGroupModal({ mode: "create", name: "" })} className="px-3 py-1.5 border border-dashed border-gray-300 text-gray-600 rounded-lg text-sm hover:border-teal-500 hover:text-teal-600">
                <i className="ri-add-line mr-1" />新建组
              </button>
            </div>
            {groups.map((g) => {
              const list = filteredScenes.filter((sc) => sc.groupId === g.id);
              const collapsed = collapsedGroups.has(g.id);
              return (
                <div key={g.id} className="border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2 px-4 py-3">
                    <button onClick={() => toggleCollapse(g.id)} className="flex items-center gap-2 text-sm font-medium text-gray-800 min-w-0">
                      <i className={collapsed ? "ri-arrow-right-s-line" : "ri-arrow-down-s-line"} />
                      <i className="ri-folder-2-line text-gray-400" />
                      <span className="truncate">{g.name}</span>
                      <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">{g.count}</span>
                    </button>
                    <div className="ml-auto flex items-center gap-3 text-sm">
                      <button onClick={() => { setCreateGroupId(g.id); setShowCreate(true); }} className="text-teal-600 hover:text-teal-700"><i className="ri-add-line" />新建</button>
                      <button onClick={() => setGroupModal({ mode: "rename", id: g.id, name: g.name })} className="text-gray-400 hover:text-gray-600" title="改名"><i className="ri-edit-line" /></button>
                      <button onClick={() => deleteGroup(g)} className="text-gray-400 hover:text-red-500" title="删除组"><i className="ri-delete-bin-line" /></button>
                    </div>
                  </div>
                  {!collapsed && (list.length > 0 ? sceneTable(list) : <p className="px-4 pb-4 text-sm text-gray-400">组内暂无点位</p>)}
                </div>
              );
            })}
            {(() => {
              const ungrouped = filteredScenes.filter((sc) => !sc.groupId);
              if (ungrouped.length === 0) return null;
              const collapsed = collapsedGroups.has("__ungrouped__");
              return (
                <div className="border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2 px-4 py-3">
                    <button onClick={() => toggleCollapse("__ungrouped__")} className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <i className={collapsed ? "ri-arrow-right-s-line" : "ri-arrow-down-s-line"} />
                      <i className="ri-folder-line text-gray-400" />
                      未分组
                      <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">{ungrouped.length}</span>
                    </button>
                  </div>
                  {!collapsed && sceneTable(ungrouped)}
                </div>
              );
            })()}
          </div>
          )
        ) : (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-map-2-line text-gray-400 text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">航线地图视图</h3>
            <p className="text-sm text-gray-500 mb-4">在此视图中以地图打点显示各点位位置和触发半径</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-md mx-auto">
              {filteredScenes.slice(0, 6).map((scene) => (
                <div key={scene.id} className="bg-white border border-gray-200 rounded-lg p-3 text-left">
                  <span className="text-sm font-medium text-gray-900">{scene.name}</span>
                  <span className="text-xs text-gray-400 block mt-1">{scene.lat.toFixed(4)}, {scene.lng.toFixed(4)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <SceneFormModal
          groups={groups}
          defaultGroupId={createGroupId}
          onClose={() => { setShowCreate(false); setCreateGroupId(""); }}
          onSaved={() => { reload(); reloadGroups(); }}
        />
      )}
      {editTarget && (
        <SceneFormModal scene={editTarget} groups={groups} onClose={() => setEditTarget(null)} onSaved={() => { reload(); reloadGroups(); }} />
      )}
      {groupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setGroupModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-96 p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900">{groupModal.mode === "create" ? "新建分组" : "分组改名"}</h3>
            <input
              autoFocus
              value={groupModal.name}
              onChange={(e) => setGroupModal({ ...groupModal, name: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && saveGroupModal()}
              placeholder="分组名称，如：西陵峡段"
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
            <div className="flex gap-2">
              <button onClick={() => setGroupModal(null)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">取消</button>
              <button onClick={saveGroupModal} className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScenesPage;